import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import AnswerReviewModal from '../../../components/AnswerReviewModal/AnswerReviewModal';
import StageHud from '../../../components/StageHud/StageHud';
import { GAME_COPY, GAME_KEYS, STAGE1_ANSWERS } from '../../../data/games';
import { finishGame } from '../../../utils/gameSave';
import { useCarnivalTimer } from '../../../hooks/useCarnivalTimer';
import { useStagePoints } from '../../../hooks/useStagePoints';
import './BraillePage.css';

export default function BraillePage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const copy = GAME_COPY.stage1;
  const [values, setValues] = useState(() =>
    Object.fromEntries(STAGE1_ANSWERS.map((a) => [a.id, '']))
  );
  const [status, setStatus] = useState(() =>
    Object.fromEntries(STAGE1_ANSWERS.map((a) => [a.id, null]))
  );
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const doneRef = useRef(false);

  const score = useMemo(() => {
    return STAGE1_ANSWERS.reduce((n, a) => {
      const v = (values[a.id] || '').trim().toLowerCase();
      return n + (v === a.value ? 1 : 0);
    }, 0);
  }, [values]);

  const { displayPoints, alreadyPlayed, markPlayedAndSyncCoins } = useStagePoints(GAME_KEYS.stage1, score);
  const { display: timerDisplay, elapsedRef } = useCarnivalTimer({ running: !alreadyPlayed });

  const reviewLines = useMemo(
    () =>
      STAGE1_ANSWERS.map((a) => {
        const userVal = (values[a.id] || '').trim();
        if (userVal.toLowerCase() === a.value) return a.label;
        return `${userVal}:- ${a.label}`;
      }),
    [values]
  );

  const markField = (id, raw) => {
    const answer = STAGE1_ANSWERS.find((a) => a.id === id);
    if (!answer) return;
    const trimmed = raw.trim();
    if (!trimmed) {
      setStatus((s) => ({ ...s, [id]: null }));
      return;
    }
    setStatus((s) => ({
      ...s,
      [id]: trimmed.toLowerCase() === answer.value ? 'correct' : 'wrong',
    }));
  };

  const onChange = (id, raw) => {
    setValues((v) => ({ ...v, [id]: raw }));
    markField(id, raw);
  };

  const openReview = async () => {
    for (const a of STAGE1_ANSWERS) {
      if (!(values[a.id] || '').trim()) {
        await Swal.fire({
          title: 'Please fill all answers',
          icon: 'warning',
          confirmButtonText: 'OK',
          customClass: { confirmButton: 'hf-swal-close', popup: 'hf-swal-popup' },
          buttonsStyling: false,
        });
        return;
      }
    }
    setReviewOpen(true);
  };

  const submit = async () => {
    if (doneRef.current || busy || !user) return;
    doneRef.current = true;
    setBusy(true);
    setReviewOpen(false);
    try {
      const result = await finishGame({
        user,
        gameKey: GAME_KEYS.stage1,
        score,
        elapsedSeconds: elapsedRef.current,
      });
      markPlayedAndSyncCoins(result);
      if (result.alreadyPlayed) navigate('/hub');
      else navigate(`/thank-you?game=${GAME_KEYS.stage1}&points=${result.coinsEarned ?? 0}`);
    } catch (err) {
      console.error(err);
      doneRef.current = false;
      setBusy(false);
      Swal.fire({
        title: 'Could not save score',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'hf-swal-close', popup: 'hf-swal-popup' },
        buttonsStyling: false,
      });
    }
  };

  return (
    <div
      className="braille-page stage-bg"
      style={{
        '--ic-bg': 'url(/img/BG_2.jpg)',
        '--ic-art': 'url(/stage1/img/Braille_BG.jpg)',
        '--ic-art-mob': 'url(/stage1/img/Braille_BG_mob.png)',
      }}
    >
      <div className="braille-stage-art" aria-hidden="true" />
      <div className="braille-alldata">
        <StageHud
          title={copy.title}
          instruction={copy.instruction}
          timerDisplay={timerDisplay}
          points={displayPoints}
          rules={copy.rules}
        />
        <div className="braille-grid">
          {STAGE1_ANSWERS.map((a) => (
            <div key={a.id} className="braille-item">
              <img
                src={a.img}
                alt=""
                className="braille-img"
              />
              <input
                type="text"
                value={values[a.id]}
                onChange={(e) => onChange(a.id, e.target.value)}
                className={`input-text1 ${status[a.id] || ''}`}
                readOnly={status[a.id] === 'correct'}
                autoComplete="off"
              />
            </div>
          ))}
        </div>
        <img
          src="/stage1/img/braille.png"
          alt="Braille A–Z reference chart"
          className="braille-alphabet-hint"
        />
        <button type="button" className="button-login1" disabled={busy} onClick={openReview}>
          Submit
        </button>
      </div>

      {reviewOpen && (
        <AnswerReviewModal title="Correct answers" lines={reviewLines} onConfirm={submit} />
      )}
    </div>
  );
}
